/**
 * <IssueDetail> — one issue, for whoever is looking at it.
 *
 * The SAME component serves the reporter and the vendor team; what differs is
 * what the server sends. A reporter's detail carries the public timeline and
 * the events they may fire (withdraw, close, reopen); the team's also carries
 * the analysis, the build, QC results and pipeline runs, which render as the
 * stage cards in `IssueTeamCards`.
 *
 * Nothing here knows the lifecycle: the buttons are `issue.actions` (what the
 * state machine allows THIS caller now) and the labels come from
 * `meta.lifecycle`. A reply while the issue is waiting on its reporter is
 * their answer — the server decides that and re-runs triage with it.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  AlertTriangle, ArrowLeft, Bot, ChevronDown, ChevronRight, Compass, Eye, EyeOff, FileText,
  HelpCircle, Lock, MessageSquare, Paperclip, RefreshCw, Send, Tag, User,
} from 'lucide-react'

import type {
  IssueAction, IssueDetail as IssueDetailData, IssueEvent, SupportMeta, SupportNotify,
  SupportRuntime,
} from './contract'
import { ATTACHMENT_ACCEPT, attachmentProblem } from './screenshot'
import ContextTimeline from './ContextTimeline'
import {
  AgentRunsCard, AnalysisCard, BuildCard, JobsCard, PLAN_EVENTS, PlanCard, QC_EVENTS, QcCard,
} from './IssueTeamCards'
import {
  ErrorLine, Pill, Spinner, SupportScope, btn, errorText, fmtDateTime, inputStyle, labelStyle,
  mutedText, openBlob, severityTone, stateLabel, statusTone, timeAgo, vocabLabel,
} from './ui'

export interface IssueDetailProps {
  runtime: SupportRuntime
  number: number
  /** Lifecycle + vocabularies; fetched here when absent. */
  meta?: SupportMeta | null
  onBack?: () => void
  /** QC's "Open staging": the host opens the app on that fix's staging copy. */
  onOpenStaging?: (issueNumber: number) => void
  notify?: SupportNotify
  /** Lifts the latest detail (a board refreshes its card from it). */
  onChanged?: (issue: IssueDetailData) => void
  /** Refresh cadence while the issue is still moving; 0 disables. */
  pollMs?: number
}

interface Question { question: string; suggested?: string }

type PatchField = 'issue_type' | 'severity' | 'priority' | 'labels' | 'title'

function questionsOf(ev: IssueEvent): Question[] {
  const raw = (ev.payload?.questions as unknown[]) || []
  return raw
    .map((q) => (q && typeof q === 'object'
      ? { question: String((q as Question).question || ''), suggested: (q as Question).suggested }
      : { question: String(q || '') }))
    .filter((q) => q.question)
}

export default function IssueDetail({
  runtime, number, meta: metaProp, onBack, onOpenStaging, notify, onChanged, pollMs = 20000,
}: IssueDetailProps) {
  const [meta, setMeta] = useState<SupportMeta | null>(metaProp || null)
  const [issue, setIssue] = useState<IssueDetailData | null>(null)
  const [loadError, setLoadError] = useState('')
  const [actionError, setActionError] = useState('')
  const [busy, setBusy] = useState(false)
  // An action that needs a "why" opens a comment box before it is fired.
  const [pending, setPending] = useState<IssueAction | null>(null)
  const [pendingComment, setPendingComment] = useState('')
  const [reply, setReply] = useState('')
  const [internal, setInternal] = useState(false)
  const [replyBusy, setReplyBusy] = useState(false)
  const [replyError, setReplyError] = useState('')
  const [showContext, setShowContext] = useState(false)
  const [attaching, setAttaching] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)

  useEffect(() => { if (metaProp) setMeta(metaProp) }, [metaProp])
  useEffect(() => {
    if (meta) return
    let cancelled = false
    runtime.meta().then((m) => { if (!cancelled) setMeta(m) }).catch(() => {
      // Labels fall back to the raw ids; the issue itself still loads.
    })
    return () => { cancelled = true }
  }, [meta, runtime])

  const apply = useCallback((next: IssueDetailData) => {
    setIssue(next)
    onChanged?.(next)
  }, [onChanged])

  const load = useCallback(async (quiet = false) => {
    try {
      const d = await runtime.getIssue(number)
      apply(d)
      setLoadError('')
    } catch (e) {
      if (!quiet) setLoadError(errorText(e, 'The issue could not be loaded.'))
    }
  }, [runtime, number, apply])

  useEffect(() => { setIssue(null); void load() }, [load])

  // Keep a moving issue fresh — the pipeline advances it without anybody
  // clicking. A finished one never changes on its own, so it is not polled.
  const terminal = !!(issue && meta?.lifecycle.terminal.includes(issue.status))
  const loaded = !!issue
  useEffect(() => {
    if (!pollMs || !loaded || terminal) return undefined
    const id = window.setInterval(() => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return
      void load(true)
    }, pollMs)
    return () => window.clearInterval(id)
  }, [pollMs, loaded, terminal, load])

  const lifecycle = meta?.lifecycle
  const team = !!issue && ('analysis' in issue || 'build' in issue || 'jobs' in issue)
  const caps = issue?.my_capacities || []
  const isReporter = caps.includes('reporter')
  const answering = !!issue && issue.status === 'needs_info' && isReporter

  // Buttons placed on a stage card are not repeated in the general bar.
  const placed = useMemo(() => {
    const s = new Set<string>()
    if (issue?.analysis) PLAN_EVENTS.forEach((e) => s.add(e))
    if (runtime.qc) QC_EVENTS.forEach((e) => s.add(e))
    return s
  }, [issue?.analysis, runtime.qc])
  const generalActions = (issue?.actions || []).filter((a) => !placed.has(a.event))

  const fire = async (a: IssueAction, comment?: string) => {
    if (!issue) return
    setBusy(true)
    setActionError('')
    try {
      const next = await runtime.transition(issue.number, a.event, comment)
      apply(next)
      setPending(null)
      setPendingComment('')
      notify?.success(`${a.label} — #${issue.number} is now ${stateLabel(lifecycle, next.status).toLowerCase()}.`)
    } catch (e) {
      setActionError(errorText(e, `Could not ${a.label.toLowerCase()}.`))
    } finally {
      setBusy(false)
    }
  }

  const onAction = (a: IssueAction) => {
    if (a.needs_comment) {
      setPending(a)
      setPendingComment('')
      setActionError('')
      return
    }
    void fire(a)
  }

  const send = async () => {
    if (!issue || !reply.trim()) return
    setReplyBusy(true)
    setReplyError('')
    try {
      const next = await runtime.comment(issue.number, reply.trim(), { internal: team && internal })
      apply(next)
      setReply('')
      if (answering) notify?.success('Thanks — your answer was sent and the issue is being looked at again.')
    } catch (e) {
      setReplyError(errorText(e, 'Your message could not be sent.'))
    } finally {
      setReplyBusy(false)
    }
  }

  const openAttachment = async (attId: number, filename: string) => {
    if (!issue) return
    try {
      if (runtime.downloadAttachment) {
        openBlob(await runtime.downloadAttachment(issue.number, attId), filename)
      } else if (runtime.attachmentUrl) {
        window.open(runtime.attachmentUrl(issue.number, attId), '_blank', 'noopener')
      }
    } catch (e) {
      notify?.error(errorText(e, 'The attachment could not be opened.'))
    }
  }

  // Evidence for a comment, an answer or a QC verdict: attached to the issue
  // (the timeline records it), one file at a time, checked before upload.
  const attach = async (files: FileList | null) => {
    if (!issue || !files?.length || !runtime.uploadAttachment) return
    setAttaching(true)
    setReplyError('')
    const problems: string[] = []
    for (const f of Array.from(files)) {
      const p = attachmentProblem(f)
      if (p) { problems.push(p); continue }
      try {
        await runtime.uploadAttachment(issue.number, f, f.name)
      } catch (e) {
        problems.push(`${f.name}: ${errorText(e, 'upload failed')}`)
      }
    }
    setAttaching(false)
    if (problems.length) setReplyError(problems.join('\n'))
    await load(true)
  }

  const patch = async (field: PatchField, value: unknown) => {
    if (!issue || !runtime.patchIssue) return
    try {
      const res = await runtime.patchIssue(issue.number, { [field]: value })
      apply(res.issue)
    } catch (e) {
      notify?.error(errorText(e, 'The change could not be saved.'))
    }
  }

  // The latest open question — its suggested answers pre-fill the reply.
  const lastQuestion = useMemo(() => {
    const qs = (issue?.events || []).filter((e) => e.kind === 'question')
    return qs.length ? qs[qs.length - 1] : null
  }, [issue?.events])
  const suggested = lastQuestion ? questionsOf(lastQuestion).filter((q) => q.suggested) : []

  if (loadError && !issue) {
    return (
      <SupportScope>
        {onBack && <button type="button" style={btn('ghost', true)} onClick={onBack}><ArrowLeft size={13} /> Back</button>}
        <ErrorLine message={loadError} onRetry={() => { void load() }} />
      </SupportScope>
    )
  }
  if (!issue) {
    return <SupportScope><div style={{ display: 'flex', gap: 8, padding: '1rem 0' }}><Spinner /> Loading issue…</div></SupportScope>
  }

  const ctx = issue.context || {}
  const steps = typeof ctx.steps === 'string' ? ctx.steps : ''

  return (
    <SupportScope>
      {/* ── header ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        {onBack && (
          <button type="button" style={btn('ghost', true)} onClick={onBack} title="Back to the list">
            <ArrowLeft size={13} />
          </button>
        )}
        <span style={{ ...mutedText, fontWeight: 700 }}>#{issue.number}</span>
        <Pill tone={statusTone(lifecycle, issue.status)}>{stateLabel(lifecycle, issue.status)}</Pill>
        {issue.sla_overdue && <Pill tone="error"><AlertTriangle size={11} /> SLA overdue</Pill>}
        <span style={{ flex: 1 }} />
        <button type="button" style={btn('ghost', true)} onClick={() => { void load() }} title="Refresh">
          <RefreshCw size={12} />
        </button>
        {runtime.watch && !isReporter && (
          <WatchToggle runtime={runtime} issue={issue} notify={notify}
            initial={meta?.me.user_id ? issue.watchers.includes(meta.me.user_id) : null} />
        )}
      </div>
      {team && runtime.patchIssue
        ? <EditableTitle title={issue.title} onSave={(t) => patch('title', t)} />
        : <h3 style={{ margin: '0 0 6px', fontSize: '1.1125rem', lineHeight: 1.3 }}>{issue.title}</h3>}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginBottom: 10 }}>
        <Pill>{vocabLabel(meta, 'issue_types', issue.type)}</Pill>
        <Pill tone={severityTone(meta, issue.severity)}>{vocabLabel(meta, 'severities', issue.severity)}</Pill>
        {issue.priority && <Pill tone="accent">{issue.priority}</Pill>}
        {issue.risk && <Pill tone={issue.risk === 'high' ? 'error' : issue.risk === 'medium' ? 'warning' : 'success'}>{issue.risk} risk</Pill>}
        {issue.labels.map((l) => <Pill key={l}><Tag size={10} /> {l}</Pill>)}
        <span style={mutedText}>
          <User size={11} style={{ verticalAlign: -1 }} /> {issue.reporter_name || 'Unknown'}
          {issue.reporter_email ? ` <${issue.reporter_email}>` : ''} · {timeAgo(issue.created_at)}
          {issue.affects_release ? ` · on v${issue.affects_release}` : ''}
        </span>
      </div>
      {issue.fixed_in_release && (
        <div style={{ ...mutedText, marginBottom: 6 }}>
          Fixed in <strong>v{issue.fixed_in_release.version}</strong>
          {issue.fixed_in_release.released_at ? ` (${fmtDateTime(issue.fixed_in_release.released_at)})` : ''}
        </div>
      )}
      {issue.duplicate_of && (
        <div style={{ ...mutedText, marginBottom: 6 }}>
          Duplicate of #{issue.duplicate_of.number} — {issue.duplicate_of.title}
          {' '}({stateLabel(lifecycle, issue.duplicate_of.status)})
        </div>
      )}

      {/* ── team triage fields ── */}
      {team && runtime.patchIssue && (
        <TriageFields issue={issue} meta={meta} runtime={runtime} onPatch={patch} />
      )}

      {/* ── description ── */}
      {issue.description && (
        <div style={{ whiteSpace: 'pre-wrap', marginBottom: 10 }}>{issue.description}</div>
      )}
      {steps && (
        <div style={{ marginBottom: 10 }}>
          <div style={labelStyle}>Steps to reproduce</div>
          <div style={{ whiteSpace: 'pre-wrap' }}>{steps}</div>
        </div>
      )}
      {team && Object.keys(ctx).length > 0 && (
        <ContextTimeline context={ctx} reportedAt={issue.created_at} />
      )}
      {Object.keys(ctx).length > 0 && (
        <div style={{ marginBottom: 10 }}>
          <button type="button" onClick={() => setShowContext((v) => !v)} style={{
            display: 'flex', alignItems: 'center', gap: 4, background: 'none', border: 'none',
            cursor: 'pointer', color: 'var(--sp-muted)', padding: 0, fontSize: '0.8425rem',
          }}>
            {showContext ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
            {team ? 'Raw captured context' : 'Captured context'}
            {ctx.obj_name ? ` · ${String(ctx.obj_name)}` : ''}
            {Array.isArray(ctx.client_errors) && ctx.client_errors.length ? ` · ${ctx.client_errors.length} error(s)` : ''}
            {Array.isArray(ctx.failed_calls) && ctx.failed_calls.length ? ` · ${ctx.failed_calls.length} failed call(s)` : ''}
          </button>
          {showContext && (
            <pre style={{
              margin: '6px 0 0', maxHeight: 260, overflow: 'auto', fontSize: '0.8125rem',
              background: 'var(--sp-bg2)', border: '1px solid var(--sp-border)', borderRadius: 8,
              padding: '0.5rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
            }}>{JSON.stringify(ctx, null, 2)}</pre>
          )}
        </div>
      )}
      {issue.attachments.length > 0 && (
        <div style={{ marginBottom: 10 }}>
          <div style={labelStyle}>Attachments</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {issue.attachments.map((a) => (
              <button key={a.id} type="button" style={btn('subtle', true)}
                disabled={!runtime.downloadAttachment && !runtime.attachmentUrl}
                onClick={() => { void openAttachment(a.id, a.filename) }}>
                <Paperclip size={12} /> {a.filename}
                <span style={mutedText}>{Math.ceil(a.size / 1024)} KB</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ── what this caller may do now ── */}
      {generalActions.length > 0 && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', margin: '4px 0 10px' }}>
          {generalActions.map((a) => {
            const ending = lifecycle?.terminal.includes(a.to)
            return (
              <button key={a.event} type="button" disabled={busy}
                style={btn(ending ? 'ghost' : 'subtle', true)} onClick={() => onAction(a)}
                title={`Moves the issue to “${stateLabel(lifecycle, a.to)}”`}>
                {a.label}
              </button>
            )
          })}
        </div>
      )}
      {pending && (
        <div style={{
          border: '1px solid var(--sp-border)', borderRadius: 8, padding: '0.6rem', marginBottom: 10,
          background: 'var(--sp-bg2)',
        }}>
          <label style={labelStyle} htmlFor={`twsp-why-${issue.number}`}>{pending.label} — say why</label>
          <textarea id={`twsp-why-${issue.number}`} value={pendingComment} rows={3} autoFocus
            onChange={(e) => setPendingComment(e.target.value)} style={{ ...inputStyle, resize: 'vertical' }} />
          <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', marginTop: 6 }}>
            <button type="button" style={btn('ghost', true)} onClick={() => setPending(null)} disabled={busy}>Cancel</button>
            <button type="button" style={btn('primary', true)} disabled={busy || !pendingComment.trim()}
              onClick={() => { void fire(pending, pendingComment.trim()) }}>
              {busy ? <Spinner size={12} /> : null} {pending.label}
            </button>
          </div>
        </div>
      )}
      <ErrorLine message={actionError} />

      {/* ── the team's stage cards ── */}
      {team && (
        <div style={{ margin: '6px 0 12px' }}>
          {issue.analysis && <AnalysisCard analysis={issue.analysis} runtime={runtime} />}
          {issue.analysis && (
            <PlanCard analysis={issue.analysis} actions={issue.actions} onAction={onAction} busy={busy} />
          )}
          {issue.build && <BuildCard build={issue.build} runtime={runtime} number={issue.number} />}
          {(issue.build || issue.analysis) && (
            <QcCard issue={issue} runtime={runtime} onSaved={apply}
              onOpenStaging={onOpenStaging} notify={notify} />
          )}
          {issue.jobs && (
            <JobsCard jobs={issue.jobs} runtime={runtime} number={issue.number}
              canRetry={caps.includes('dev') || caps.includes('pm')}
              stages={issue.retry_stages}
              notify={notify} onQueued={() => { void load(true) }} />
          )}
          {issue.agent_runs && issue.agent_runs.length > 0 && (
            <AgentRunsCard runs={issue.agent_runs} runtime={runtime} />
          )}
        </div>
      )}

      {/* ── timeline ── */}
      <div style={labelStyle}>Activity</div>
      <ol style={{ listStyle: 'none', padding: 0, margin: '0 0 12px' }}>
        {issue.events.map((ev) => <TimelineRow key={ev.id} ev={ev} meta={meta} />)}
      </ol>

      {/* ── reply ── */}
      <div style={{
        border: `1px solid ${answering ? 'var(--sp-accent)' : 'var(--sp-border)'}`,
        borderRadius: 10, padding: '0.6rem',
      }}>
        <label style={labelStyle} htmlFor={`twsp-reply-${issue.number}`}>
          {answering ? 'Your answer' : 'Add a comment'}
        </label>
        {answering && suggested.length > 0 && !reply && (
          <button type="button" style={{ ...btn('ghost', true), marginBottom: 6 }}
            onClick={() => setReply(suggested.map((q) => `${q.question}\n${q.suggested}`).join('\n\n'))}>
            Use the suggested answers
          </button>
        )}
        <textarea id={`twsp-reply-${issue.number}`} value={reply} rows={answering ? 4 : 3}
          onChange={(e) => setReply(e.target.value)} style={{ ...inputStyle, resize: 'vertical' }}
          placeholder={answering ? 'Answer the questions above' : 'Write a comment…'} />
        <ErrorLine message={replyError} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
          {team && !answering && (
            <label style={{ display: 'flex', alignItems: 'center', gap: 5, ...mutedText, cursor: 'pointer' }}
              title="Internal notes are seen by the support team only, never by the reporter">
              <input type="checkbox" checked={internal} onChange={(e) => setInternal(e.target.checked)} />
              <Lock size={11} /> Internal note
            </label>
          )}
          <span style={{ flex: 1 }} />
          {runtime.uploadAttachment && (
            <>
              <input ref={fileRef} type="file" multiple accept={ATTACHMENT_ACCEPT} style={{ display: 'none' }}
                onChange={(e) => { void attach(e.target.files); e.target.value = '' }} />
              <button type="button" style={btn('ghost', true)} disabled={attaching}
                onClick={() => fileRef.current?.click()} title="Attach a screenshot or file to this issue">
                {attaching ? <Spinner size={12} /> : <Paperclip size={12} />} Attach
              </button>
            </>
          )}
          <button type="button" style={btn('primary', true)} disabled={replyBusy || !reply.trim()}
            onClick={() => { void send() }}>
            {replyBusy ? <Spinner size={12} /> : <Send size={12} />} {answering ? 'Send answer' : 'Send'}
          </button>
        </div>
      </div>
    </SupportScope>
  )
}

// ── pieces ─────────────────────────────────────────────────────────────────

function WatchToggle({ runtime, issue, notify, initial }: {
  runtime: SupportRuntime
  issue: IssueDetailData
  notify?: SupportNotify
  /** From `watchers` ∋ `meta.me.user_id`; null when the id is unknown. */
  initial: boolean | null
}) {
  const [watching, setWatching] = useState<boolean | null>(initial)
  useEffect(() => { setWatching(initial) }, [initial])
  const [busy, setBusy] = useState(false)
  const toggle = async () => {
    if (!runtime.watch) return
    setBusy(true)
    try {
      const res = await runtime.watch(issue.number, !watching)
      setWatching(res.watching)
      notify?.success(res.watching ? 'You will be told about changes to this issue.' : 'Stopped watching.')
    } catch (e) {
      notify?.error(errorText(e, 'Could not change watching.'))
    } finally {
      setBusy(false)
    }
  }
  return (
    <button type="button" style={btn('ghost', true)} disabled={busy} onClick={() => { void toggle() }}
      title={watching ? 'Stop watching this issue' : 'Be notified about changes to this issue'}>
      {watching ? <EyeOff size={12} /> : <Eye size={12} />} {watching ? 'Unwatch' : 'Watch'}
    </button>
  )
}

function EditableTitle({ title, onSave }: { title: string; onSave: (t: string) => void }) {
  const [editing, setEditing] = useState(false)
  const [value, setValue] = useState(title)
  useEffect(() => { if (!editing) setValue(title) }, [title, editing])
  const commit = () => {
    const t = value.trim()
    setEditing(false)
    if (t && t !== title) onSave(t)
    else setValue(title)
  }
  if (!editing) {
    return (
      <h3 style={{ margin: '0 0 6px', fontSize: '1.1125rem', lineHeight: 1.3, cursor: 'text' }}
        title="Click to edit the title" onClick={() => setEditing(true)}>
        {title}
      </h3>
    )
  }
  return (
    <input value={value} autoFocus maxLength={300} aria-label="Issue title"
      onChange={(e) => setValue(e.target.value)} onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') commit()
        if (e.key === 'Escape') { setValue(title); setEditing(false) }
      }}
      style={{ ...inputStyle, fontSize: '1.0625rem', fontWeight: 700, marginBottom: 6 }} />
  )
}

function TriageFields({ issue, meta, runtime, onPatch }: {
  issue: IssueDetailData
  meta: SupportMeta | null
  runtime: SupportRuntime
  onPatch: (field: PatchField, value: unknown) => void
}) {
  // Priorities come with the public policy; a server that predates that is
  // asked for its full policy instead.
  const served = meta?.policy.priorities
  const [priorities, setPriorities] = useState<string[]>(served || [])
  const [labels, setLabels] = useState(issue.labels.join(', '))
  useEffect(() => { setLabels(issue.labels.join(', ')) }, [issue.labels])
  useEffect(() => {
    if (served) { setPriorities(served); return undefined }
    if (!runtime.getPolicy) return undefined
    let cancelled = false
    runtime.getPolicy()
      .then((p) => { if (!cancelled) setPriorities(p.policy.priorities || []) })
      .catch(() => { /* priority stays read-only */ })
    return () => { cancelled = true }
  }, [runtime, served])

  const small = { ...inputStyle, padding: '0.25rem 0.4rem', fontSize: '0.8425rem' }
  return (
    <div style={{
      display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 8,
      marginBottom: 10, padding: '0.5rem', borderRadius: 8, background: 'var(--sp-bg2)',
    }}>
      <div>
        <label style={labelStyle}>Type</label>
        <select value={issue.type} style={small} onChange={(e) => onPatch('issue_type', e.target.value)}>
          {(meta?.policy.issue_types || []).map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
        </select>
      </div>
      <div>
        <label style={labelStyle}>Severity</label>
        <select value={issue.severity} style={small} onChange={(e) => onPatch('severity', e.target.value)}>
          {(meta?.policy.severities || []).map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
        </select>
      </div>
      <div>
        <label style={labelStyle}>Priority</label>
        <select value={issue.priority || ''} style={small} disabled={!priorities.length}
          onChange={(e) => onPatch('priority', e.target.value || null)}>
          <option value="">—</option>
          {priorities.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
      </div>
      <div>
        <label style={labelStyle}>Labels</label>
        <input value={labels} style={small} onChange={(e) => setLabels(e.target.value)}
          onBlur={() => {
            const next = labels.split(',').map((s) => s.trim()).filter(Boolean)
            if (next.join(',') !== issue.labels.join(',')) onPatch('labels', next)
          }}
          placeholder="comma, separated" />
      </div>
    </div>
  )
}

function eventIcon(kind: string) {
  if (kind === 'ai_note') return <Bot size={13} />
  if (kind === 'question') return <HelpCircle size={13} />
  if (kind === 'attachment') return <Paperclip size={13} />
  if (kind === 'comment' || kind === 'answer') return <MessageSquare size={13} />
  if (kind === 'direction') return <Compass size={13} />
  return <FileText size={13} />
}

function TimelineRow({ ev, meta }: { ev: IssueEvent; meta: SupportMeta | null }) {
  const lifecycle = meta?.lifecycle
  const who = ev.actor_name || (ev.actor_kind === 'agent' ? 'Support AI' : ev.actor_kind === 'system' ? 'Pipeline' : 'Someone')
  let headline: string
  switch (ev.kind) {
    case 'created': headline = 'reported the issue'; break
    case 'transition':
      headline = `${stateLabel(lifecycle, ev.from)} → ${stateLabel(lifecycle, ev.to)}`
      break
    case 'question': headline = 'asked'; break
    case 'answer': headline = 'answered'; break
    case 'ai_note': headline = 'noted'; break
    case 'direction': {
      // A reviewer's decision on a failed UI acceptance test — what Support works from.
      const p = ev.payload || {}
      headline = `decided: ${String(p.decision_label || p.decision || 'fix it')}`
        + (p.target_label ? ` (change ${String(p.target_label)})` : '')
      break
    }
    case 'attachment': headline = `attached ${ev.body || 'a file'}`; break
    case 'field': {
      const p = ev.payload || {}
      const show = (v: unknown) => (Array.isArray(v) ? v.join(', ') : v === null || v === undefined || v === '' ? '—' : String(v))
      headline = `changed ${String(p.field || 'a field').replace(/_id$/, '').replace(/_/g, ' ')}: ${show(p.old)} → ${show(p.new)}`
      break
    }
    default: headline = ev.kind === 'comment' ? 'commented' : ev.kind.replace(/_/g, ' ')
  }
  const body = ev.kind === 'attachment' ? '' : (ev.body || '')
  const qs = ev.kind === 'question' ? questionsOf(ev) : []
  const corrective = typeof ev.payload?.corrective === 'string' ? ev.payload.corrective as string : ''
  const [open, setOpen] = useState(false)
  return (
    <li style={{
      display: 'flex', gap: 8, padding: '0.45rem 0', borderBottom: '1px solid var(--sp-border)',
    }}>
      <span style={{ color: ev.actor_kind === 'agent' ? 'var(--sp-accent)' : 'var(--sp-muted)', marginTop: 2 }}>
        {eventIcon(ev.kind)}
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '0.8625rem' }}>
          <strong>{who}</strong> {headline}
          {ev.visibility === 'internal' && <> <Pill tone="warning"><Lock size={9} /> internal</Pill></>}
          <span style={{ ...mutedText, marginLeft: 6 }} title={fmtDateTime(ev.at)}>{timeAgo(ev.at)}</span>
        </div>
        {body && <div style={{ whiteSpace: 'pre-wrap', marginTop: 3 }}>{body}</div>}
        {qs.length > 0 && (
          <ol style={{ margin: '4px 0 0', paddingLeft: 18 }}>
            {qs.map((q, i) => (
              <li key={i} style={{ marginBottom: 3 }}>
                {q.question}
                {q.suggested && <div style={mutedText}>Suggested: {q.suggested}</div>}
              </li>
            ))}
          </ol>
        )}
        {corrective && (
          <>
            <button type="button" style={{ ...btn('ghost', true), marginTop: 4, padding: '0 0.45rem' }}
              onClick={() => setOpen((v) => !v)}>{open ? 'Hide' : 'Show'} test feedback</button>
            {open && (
              <pre style={{
                margin: '4px 0 0', maxHeight: 220, overflow: 'auto', fontSize: '0.8125rem',
                whiteSpace: 'pre-wrap', background: 'var(--sp-bg2)', padding: '0.4rem', borderRadius: 6,
              }}>{corrective}</pre>
            )}
          </>
        )}
      </div>
    </li>
  )
}
